import { useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { PageHeader, Section, Empty } from "../components/common";
import SimpleCrud, { FormFields } from "../components/SimpleCrud";
import { api, errMsg, useFetch } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";

const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now() + Math.random()));

function SubTable({ rows, cols, onChange, addLabel, testid }) {
  const upd = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="dense-table w-full">
        <thead><tr>{cols.map((c) => <th key={c.key}>{c.label}</th>)}<th /></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id}>
              {cols.map((c) => <td key={c.key} style={{ minWidth: c.w || 120 }}>{c.options ? (
                <select data-testid={`${testid}-${c.key}-${i}`} className="cell-input" value={r[c.key] || ""} onChange={(e) => upd(i, c.key, e.target.value)}>
                  <option value="">—</option>{c.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>) : <input data-testid={`${testid}-${c.key}-${i}`} className="cell-input" value={r[c.key] || ""} onChange={(e) => upd(i, c.key, e.target.value)} />}</td>)}
              <td><button onClick={() => onChange(rows.filter((_, j) => j !== i))} className="text-neutral-400 hover:text-[#db0720]"><Trash2 className="h-3.5 w-3.5" /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button size="sm" variant="outline" className="mt-2" data-testid={`${testid}-add`} onClick={() => onChange([...rows, { id: uid() }])}><Plus className="h-3.5 w-3.5" />{addLabel}</Button>
    </div>
  );
}

function CustomerSheet({ cust, onClose, onSaved, countries }) {
  const { t } = useT();
  const [f, setF] = useState(cust);
  if (!cust) return null;
  const save = async () => {
    if (!f.name || !f.country) return toast.error(t("common.required"));
    try {
      const r = f.id ? await api.put(`/customers/${f.id}`, f) : await api.post("/customers", f);
      toast.success(t("common.saved")); setF(r.data); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const fields = [
    { key: "name", label: t("common.name"), required: true }, { key: "legal_name", label: t("cust.legal") },
    { key: "tax_id", label: t("cust.taxid") }, { key: "country", label: t("common.country"), required: true, type: "select", options: countries },
    { key: "street", label: t("cust.street"), wide: true }, { key: "neighborhood", label: t("cust.neighborhood") }, { key: "zip", label: t("cust.zip") },
    { key: "city", label: t("cust.city") }, { key: "state", label: t("cust.state") }, { key: "industry", label: t("cust.industry") },
  ];
  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-[calc(100vw-1.5rem)] lg:max-w-[calc(100vw-4rem)]" data-testid="customer-sheet">
        <SheetHeader><SheetTitle>{f.name || t("cust.new")}</SheetTitle></SheetHeader>
        <div className="mt-6 space-y-5">
          <FormFields fields={fields} f={f} setF={setF} />
          <Section title={t("cust.plants")}>
            <SubTable testid="plants" rows={f.plants || []} onChange={(v) => setF({ ...f, plants: v })} addLabel={t("cust.addPlant")}
              cols={[{ key: "name", label: t("common.name"), w: 170 }, { key: "street", label: t("cust.street"), w: 220 }, { key: "neighborhood", label: t("cust.neighborhood"), w: 170 }, { key: "zip", label: t("cust.zip"), w: 90 }, { key: "city", label: t("cust.city"), w: 130 }, { key: "state", label: t("cust.state"), w: 130 }, { key: "country", label: t("common.country"), w: 150, options: countries }]} />
          </Section>
          <Section title={t("cust.contacts")}>
            <SubTable testid="contacts" rows={f.contacts || []} onChange={(v) => setF({ ...f, contacts: v })} addLabel={t("cust.addContact")}
              cols={[{ key: "name", label: t("common.name") }, { key: "role", label: t("ws.role") }, { key: "email", label: t("cust.email") }, { key: "phone", label: t("cust.phone") }]} />
          </Section>
          <Button data-testid="customer-save-btn" onClick={save} className="w-full">{t("common.save")}</Button>
          {f.id && (
            <SimpleCrud testid="machines" endpoint={`/machines?customer_id=${f.id}`} title={t("cust.machines")} newLabel={t("mach.new")} defaults={{ customer_id: f.id }}
              columns={[{ key: "model", label: t("mach.model") }, { key: "machine_type", label: t("mach.type") }, { key: "serial_number", label: t("mach.serial") },
                { key: "year", label: t("mach.year") }, { key: "plant_id", label: t("ws.plant"), render: (r) => (f.plants || []).find((p) => p.id === r.plant_id)?.name || "—" }]}
              fields={[{ key: "model", label: t("mach.model"), required: true }, { key: "machine_type", label: t("mach.type") },
                { key: "serial_number", label: t("mach.serial"), required: true }, { key: "year", label: t("mach.year"), type: "number" },
                { key: "plant_id", label: t("ws.plant"), type: "select", options: (f.plants || []).map((p) => ({ value: p.id, label: p.name })) }]} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function Customers() {
  const { t } = useT();
  const { can } = useAuth();
  const { data, reload } = useFetch("/customers");
  const { data: machines } = useFetch("/machines");
  const { data: countries } = useFetch("/countries");
  const [q, setQ] = useState("");
  const [cust, setCust] = useState(null);
  const copts = (countries || []).map((c) => ({ value: c.code, label: `${c.name} (${c.code})` }));
  const rows = (data || []).filter((c) => !q || `${c.name} ${c.legal_name} ${c.tax_id}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <PageHeader title={t("cust.title")} kicker={t("nav.group.master")}>
        {can("master:write") && <Button data-testid="new-customer-btn" onClick={() => setCust({ plants: [], contacts: [] })}><Plus className="h-4 w-4" />{t("cust.new")}</Button>}
      </PageHeader>
      <Input data-testid="customers-search-input" placeholder={t("common.search")} value={q} onChange={(e) => setQ(e.target.value)} className="mb-4 h-9 max-w-xs bg-white" />
      <div className="overflow-x-auto rounded-md border border-neutral-200 bg-white">
        <table className="dense-table w-full" data-testid="customers-table">
          <thead><tr><th>{t("common.name")}</th><th>{t("cust.legal")}</th><th>{t("cust.taxid")}</th><th>{t("common.country")}</th><th>{t("cust.plants")}</th><th>{t("cust.contacts")}</th><th>{t("cust.machines")}</th><th /></tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="cursor-pointer" onClick={() => setCust(c)} data-testid={`customer-row-${c.id}`}>
                <td className="font-semibold">{c.name}</td><td className="text-neutral-600">{c.legal_name}</td><td className="font-mono">{c.tax_id}</td><td>{c.country}</td>
                <td>{(c.plants || []).length}</td><td>{(c.contacts || []).length}</td><td>{(machines || []).filter((m) => m.customer_id === c.id).length}</td>
                <td className="text-right"><Button size="sm" variant="outline" data-testid={`customer-edit-${c.id}`}><Pencil className="h-3.5 w-3.5" />{can("master:write") ? t("common.edit") : t("common.view")}</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && !rows.length && <Empty>{t("common.empty")}</Empty>}
      </div>
      {cust && <CustomerSheet cust={cust} countries={copts} onClose={() => { setCust(null); reload(); }} onSaved={reload} />}
    </div>
  );
}
